import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { useScrollDirection } from "@/hooks/use-scroll-direction";
import { navGroups, platformItems, solutionsItems, companyItems } from "@/lib/nav-data";
import { Button } from "@/components/ui/Button";
import { MobileNav } from "./MobileNav";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLanguageTransition } from "@/components/LanguageTransitionProvider";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

/**
 * Sticky white site header with the official Frac logo. Hides on scroll-down,
 * reveals on scroll-up. Mega-menu dropdowns remain dark (surface-raised) so
 * they read cleanly hovering over the dark body below.
 */
export function SiteNav({ variant = "on-dark" }) {
  const { direction, scrollY } = useScrollDirection();
  const hidden = direction === "down" && scrollY > 140;
  const scrolled = scrollY > 20;

  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useT("nav");
  const locale = useLocale();
  const { start } = useLanguageTransition();

  // Rebuild the nav data with locale-aware hrefs and translated labels.
  const localisedGroups = useMemo(() => {
    const localiseHref = (base) => addLocalePrefix(base, locale);
    const localisePlatform = platformItems.map((p) => ({
      ...p,
      href: localiseHref(p.hrefBase),
      label: t(`platformItems.${p.key}.label`, { defaultValue: p.label }),
      desc: t(`platformItems.${p.key}.desc`, { defaultValue: p.desc }),
    }));
    const localiseSolutions = solutionsItems.map((s) => ({
      ...s,
      href: localiseHref(s.hrefBase),
      label: t(`solutionsItems.${s.key}.label`, { defaultValue: s.label }),
      desc: t(`solutionsItems.${s.key}.desc`, { defaultValue: s.desc }),
    }));
    const localiseCompany = companyItems.map((c) => {
      const localised = {
        ...c,
        href: localiseHref(c.hrefBase),
        label: t(`companyItems.${c.key}`, { defaultValue: c.label }),
      };
      // Only the Verified Documents item carries a description; other
      // Company items render as label-only. Keep the branch cheap so
      // the four pre-existing items stay unaffected.
      if (c.desc) {
        localised.desc = t(`companyItemsDesc.${c.key}`, {
          defaultValue: c.desc,
        });
      }
      return localised;
    });
    return navGroups.map((g) => {
      if (g.items) {
        const items =
          g.id === "platform"
            ? localisePlatform
            : g.id === "solutions"
              ? localiseSolutions
              : g.id === "company"
                ? localiseCompany
                : g.items;
        return { ...g, label: t(g.id, { defaultValue: g.label }), items };
      }
      return {
        ...g,
        href: localiseHref(g.hrefBase),
        label: t(g.id, { defaultValue: g.label }),
      };
    });
  }, [locale, t]);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!openMenu) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openMenu]);

  const isLight = variant === "on-light";
  const shellBg = cn(
    "bg-white border-b",
    isLight ? "border-frac-gray-30" : "border-[rgba(2,4,11,0.08)]",
  );

  const linkIdle = "text-frac-purple/75 hover:text-frac-violet";
  const linkActive = "text-frac-violet";

  const bookDemoHref = addLocalePrefix("/contact", locale);

  return (
    <>
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-50 will-change-transform transition-transform duration-base ease-out",
          hidden ? "-translate-y-full" : "translate-y-0",
        )}
        data-testid="site-nav"
        data-variant={variant}
        data-hidden={hidden ? "true" : "false"}
      >
        <div
          className={cn(
            "transition-[background-color,border-color,box-shadow] duration-base ease-out motion-reduce:transition-none",
            shellBg,
          )}
          style={{
            boxShadow: scrolled
              ? "0 1px 3px rgba(2, 4, 11, 0.08)"
              : "0 0 0 rgba(2, 4, 11, 0)",
          }}
        >
          <div className="container-frac flex h-[56px] md:h-[64px] items-center justify-between gap-6">
            <Link
              to={addLocalePrefix("/", locale)}
              aria-label="Frac home"
              className="shrink-0 -m-2 flex items-center p-2 rounded-md"
              data-testid="nav-logo-link"
            >
              <img
                src="/frac-logo-official-nav.png"
                alt="Frac"
                width={187}
                height={40}
                className="h-8 w-auto md:h-10"
                data-testid="nav-logo-img"
              />
            </Link>

            <nav
              className="hidden lg:flex items-center gap-1"
              aria-label="Primary"
            >
              {localisedGroups.map((g) =>
                g.items ? (
                  <MegaMenuItem
                    key={g.id}
                    group={g}
                    isOpen={openMenu === g.id}
                    setOpenMenu={setOpenMenu}
                  />
                ) : (
                  <NavLink
                    key={g.id}
                    to={g.href}
                    className={({ isActive }) =>
                      cn(
                        "inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold transition-colors duration-fast ease-out",
                        isActive ? linkActive : linkIdle,
                      )
                    }
                    data-testid={`nav-link-${g.id}`}
                  >
                    {g.label}
                  </NavLink>
                ),
              )}
            </nav>

            <div className="flex items-center gap-3">
              <LanguageToggle
                variant="on-light"
                onTransitionStart={start}
                className="hidden sm:inline-flex"
              />
              <Button
                as="link"
                to={bookDemoHref}
                variant="primary"
                size="sm"
                className="hidden md:inline-flex"
                data-testid="nav-book-demo"
              >
                {t("bookDemo")}
              </Button>
              <button
                type="button"
                className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-frac-purple transition-colors duration-fast ease-out hover:bg-frac-purple/5"
                aria-label={t("openMenu")}
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen(true)}
                data-testid="nav-mobile-toggle"
              >
                <Menu className="h-5 w-5" strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </div>
      </header>
      <MobileNav
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        localisedGroups={localisedGroups}
      />
    </>
  );
}

function MegaMenuItem({ group, isOpen, setOpenMenu }) {
  const triggerRef = useRef(null);
  const itemsRef = useRef([]);
  const closeTimer = useRef(null);

  const open = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenMenu(group.id);
  }, [group.id, setOpenMenu]);

  const close = useCallback(() => {
    setOpenMenu((current) => (current === group.id ? null : current));
  }, [group.id, setOpenMenu]);

  const scheduleClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  }, [setOpenMenu]);

  useEffect(() => () => closeTimer.current && clearTimeout(closeTimer.current), []);

  const focusItem = (idx) => {
    const items = itemsRef.current.filter(Boolean);
    if (!items.length) return;
    const bounded = ((idx % items.length) + items.length) % items.length;
    items[bounded]?.focus();
  };

  const onTriggerKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
      requestAnimationFrame(() => focusItem(0));
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
      triggerRef.current?.focus();
    }
  };

  const onMenuKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      triggerRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const active = document.activeElement;
      const items = itemsRef.current.filter(Boolean);
      const idx = items.indexOf(active);
      focusItem(idx + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const active = document.activeElement;
      const items = itemsRef.current.filter(Boolean);
      const idx = items.indexOf(active);
      focusItem(idx - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusItem(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusItem(itemsRef.current.length - 1);
    }
  };

  const triggerIdle = "text-frac-purple/75 hover:text-frac-violet";

  return (
    <div
      className="relative"
      onMouseEnter={open}
      onMouseLeave={scheduleClose}
    >
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          "inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors duration-fast ease-out",
          isOpen ? "text-frac-violet bg-frac-purple-10" : triggerIdle,
        )}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={`nav-menu-panel-${group.id}`}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={onTriggerKeyDown}
        data-testid={`nav-menu-trigger-${group.id}`}
      >
        {group.label}
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-fast ease-out",
            isOpen && "rotate-180",
          )}
          strokeWidth={2}
        />
      </button>

      {isOpen && (
        <div
          id={`nav-menu-panel-${group.id}`}
          role="menu"
          aria-label={group.label}
          onKeyDown={onMenuKeyDown}
          className="absolute left-1/2 top-full mt-2 w-[560px] max-w-[92vw] -translate-x-1/2 rounded-lg border border-hairline bg-surface-raised p-6 shadow-elev-lg"
          data-testid={`nav-menu-panel-${group.id}`}
        >
          <ul className="grid gap-1">
            {group.items.map((item, i) => (
              <li key={item.href} role="none">
                <Link
                  ref={(el) => (itemsRef.current[i] = el)}
                  to={item.href}
                  role="menuitem"
                  className="group flex items-start gap-3 rounded-md p-3 transition-colors duration-fast ease-out hover:bg-white/5 focus-visible:bg-white/5"
                  onClick={() => setOpenMenu(null)}
                  data-testid={`nav-menu-item-${group.id}-${i}`}
                >
                  {item.icon ? (
                    <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-hairline bg-surface-elevated text-ink-secondary transition-colors duration-fast ease-out group-hover:border-active-border group-hover:text-frac-violet">
                      <item.icon className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                  ) : (
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-frac-violet/60" aria-hidden />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-ink-primary">
                      {item.label}
                    </div>
                    {item.desc && (
                      <div className="mt-0.5 text-xs leading-snug text-ink-tertiary">
                        {item.desc}
                      </div>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SiteNav;
